import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { motion, type Variants } from '@/components/framer-motion'
import { useTheme } from '@/contexts/theme.context'
import '@/lib/unicorn-clean'
import UnicornScene from 'unicornstudio-react'

const UNICORN_PROJECT_ID = {
  dark: 'zJdKYuArAMyin9VKBkaI',
  light: 'GPWTJYq9nXKAJ0dfGVOI',
} as const

const fadeUpVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 10,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.9,
      ease: [0.23, 1, 0.32, 1],
    },
  },
}

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.25,
      delayChildren: 0.3,
      duration: 0.9,
      ease: [0.23, 1, 0.32, 1],
    },
  },
}

export function HomePage() {
  const { resolvedTheme } = useTheme()
  const isLight = resolvedTheme === 'light'
  const [isButtonHovering, setIsButtonHovering] = useState(false)
  const [sceneLoaded, setSceneLoaded] = useState(false)
  const [showScene, setShowScene] = useState(false)

  useEffect(() => {
    if (sceneLoaded) {
      // Add a small delay after load to let the scene fully render
      const timer = setTimeout(() => {
        setShowScene(true)
      }, 300)
      return () => clearTimeout(timer)
    }
  }, [sceneLoaded])

  return (
    <div
      className={`h-screen overflow-hidden relative ${isLight ? 'bg-white' : 'bg-black'}`}
    >
      {/* Full-screen unicorn scene, sits behind the frosted wall */}
      <div
        aria-hidden
        className="absolute inset-0 z-0 transition-opacity duration-[1500ms] ease-out pointer-events-none"
        style={{ opacity: showScene ? 1 : 0 }}
      >
        <UnicornScene
          key={`bg-${resolvedTheme}`}
          projectId={
            isLight ? UNICORN_PROJECT_ID.light : UNICORN_PROJECT_ID.dark
          }
          width="100%"
          height="110%"
          scale={0.65}
          dpi={1.5}
        />
      </div>

      {/* Frosted wall — blurs the scene behind it */}
      <div
        aria-hidden
        className={`absolute inset-0 z-[1] pointer-events-none backdrop-blur-2xl ${
          isLight ? 'bg-white/60' : 'bg-black/35'
        }`}
      />

      {/* Structural grid — high-contrast lines, fades quickly near the window */}
      <div
        aria-hidden
        className="absolute inset-0 z-[2] pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(to right, ${isLight ? 'rgba(15,23,42,0.22)' : 'rgba(255,255,255,0.18)'} 1px, transparent 1px),
            linear-gradient(to bottom, ${isLight ? 'rgba(15,23,42,0.22)' : 'rgba(255,255,255,0.18)'} 1px, transparent 1px)
          `,
          backgroundSize: '44px 44px',
          maskImage:
            'radial-gradient(ellipse 60vw 60vh at center, black 0%, black 35%, transparent 90%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 60vw 60vh at center, black 0%, black 35%, transparent 90%)',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
        }}
      />

      {/* Atmospheric spotlight — subtle focus around the window */}
      <div
        aria-hidden
        className="absolute inset-0 z-[3] pointer-events-none"
        style={{
          background: isLight
            ? 'radial-gradient(ellipse 55% 60% at 50% 50%, rgba(255,255,255,0.35), transparent 70%)'
            : 'radial-gradient(ellipse 55% 60% at 50% 50%, rgba(151,240,197,0.08), transparent 70%)',
        }}
      />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="h-full relative z-10"
      >
        <div className="h-full w-full flex flex-col items-center justify-center z-20 relative">
          <div
            className={`relative overflow-hidden max-w-md sm:max-w-[900px] w-full rounded-2xl border ${
              isLight ? 'border-black/15' : 'border-white/15'
            }`}
            style={{
              boxShadow: isLight
                ? [
                    'inset 0 1px 0 rgba(255,255,255,0.6)',
                    'inset 0 0 60px rgba(15,23,42,0.06)',
                    '0 30px 60px -20px rgba(15,23,42,0.25)',
                    '0 0 80px rgba(151,240,197,0.12)',
                  ].join(', ')
                : [
                    'inset 0 1px 0 rgba(255,255,255,0.08)',
                    'inset 0 0 80px rgba(0,0,0,0.45)',
                    '0 30px 80px -20px rgba(0,0,0,0.55)',
                    '0 0 120px rgba(151,240,197,0.06)',
                  ].join(', '),
            }}
          >
            {/* Unicorn scene — clipped to this card, is the "view through the window" */}
            <div
              aria-hidden
              className="absolute inset-0 z-0 transition-opacity duration-[1500ms] ease-out pointer-events-none"
              style={{ opacity: showScene ? 1 : 0 }}
            >
              <UnicornScene
                key={resolvedTheme}
                projectId={
                  isLight ? UNICORN_PROJECT_ID.light : UNICORN_PROJECT_ID.dark
                }
                width="100%"
                height="100%"
                scale={0.65}
                dpi={1.5}
                onLoad={() => setSceneLoaded(true)}
                onError={error => {
                  console.error('UnicornScene error:', error)
                  setSceneLoaded(true)
                }}
              />
            </div>

            <div className="relative z-10 space-y-8 text-center px-8 sm:px-16 py-16 sm:py-24">
              <motion.div
                variants={fadeUpVariants}
                className="flex justify-center"
              >
                <img
                  src="/assets/images/super-logo.svg"
                  alt="Super Logo"
                  className={`h-10 w-auto ${isLight ? '' : 'invert'}`}
                />
              </motion.div>

              <motion.div variants={fadeUpVariants} className="space-y-4">
                <h1
                  className={`text-3xl font-bold hidden ${isLight ? 'text-gray-900' : 'text-white'}`}
                >
                  AI-powered communications
                </h1>
                <p
                  className={`text-xl font-semibold ${isLight ? 'text-gray-900' : 'text-gray-100'}`}
                  style={{ fontFamily: 'Fragment Mono, monospace' }}
                >
                  Your AI communication platform for property management,{' '}
                  <span className="relative inline-block font-bold">
                    <span className={isLight ? 'text-gray-900' : 'text-white'}>
                      supercharging
                    </span>
                    <motion.span
                      aria-hidden
                      initial={{ backgroundPositionX: '200%' }}
                      animate={{ backgroundPositionX: '-100%' }}
                      transition={{
                        delay: 0.6,
                        duration: 5.5,
                        ease: [0.22, 1, 0.36, 1],
                      }}
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        backgroundImage: isLight
                          ? 'linear-gradient(90deg, transparent 0%, #166534 35%, #22a06b 50%, #0f7a4a 65%, transparent 100%)'
                          : 'linear-gradient(90deg, transparent 0%, #97C2A7 35%, #d4f0e0 50%, #97f0c5 65%, transparent 100%)',
                        backgroundSize: '300% 100%',
                        backgroundRepeat: 'no-repeat',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        color: 'transparent',
                      }}
                    >
                      supercharging
                    </motion.span>
                  </span>{' '}
                  your customer experience
                </p>
              </motion.div>

              <motion.div
                variants={fadeUpVariants}
                className="w-full space-y-4"
              >
                <motion.div
                  whileTap={{ scale: 0.98 }}
                  className="w-full flex justify-center"
                >
                  <motion.div
                    className="w-full max-w-[14rem] rounded-sm"
                    animate={{
                      backgroundColor: isButtonHovering ? '#1a1a1a' : '#000000',
                    }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                  >
                    <Button
                      asChild
                      className="w-full bg-transparent hover:bg-transparent text-lg"
                      style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                      size="lg"
                      onMouseEnter={() => setIsButtonHovering(true)}
                      onMouseLeave={() => setIsButtonHovering(false)}
                    >
                      <Link to="/sign-up">Get started</Link>
                    </Button>
                  </motion.div>
                </motion.div>

                <motion.div variants={fadeUpVariants}>
                  <Link
                    to="/sign-in"
                    className={`text-base font-medium transition-colors ${
                      isLight
                        ? 'text-gray-700 hover:text-gray-900'
                        : 'text-gray-300 hover:text-white'
                    }`}
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                  >
                    or Sign in
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
        {/* <div className="fixed -bottom-70 md:-bottom-80 left-0 w-full flex items-center justify-center mx-auto z-10">
          <motion.div
            variants={fadeUpVariants}
            className="w-96 h-96 md:w-[30rem] md:h-[30rem] mx-auto mt-4"
          >
            <Suspense
              fallback={
                <div className="w-96 h-96 md:w-[30rem] md:h-[30rem] bg-gradient-to-br from-cyan-100/20 to-emerald-100/20 rounded-full animate-pulse" />
              }
            >
              <GlassOrb isHovering={isHovering} />
            </Suspense>
          </motion.div>
        </div> */}
      </motion.div>
    </div>
  )
}
